import { useContext, createContext } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import isEmpty from 'lodash/isEmpty';

import { CustomNotification } from 'common/components';

import { useVisibleModal } from 'hooks';
import { useValidateActiveProduct } from 'pages/home/ribbon/components/sections/product/components/make-product-active/hooks';

import * as minimumRequirementServices from 'services/minimumRequirements';

export const RequiredActiveProductContext = createContext();

export const useRequiredActiveProductContext = () =>
  useContext(RequiredActiveProductContext);

export const useRequiredActiveProductModal = () => {
  const [
    visibleModalRequiredActiveProduct,
    {
      onModal: onModalRequiredActiveProduct,
      offModal: offModalRequiredActiveProduct,
    },
  ] = useVisibleModal();

  return {
    visibleModalRequiredActiveProduct,
    onModalRequiredActiveProduct,
    offModalRequiredActiveProduct,
  };
};

export const minimumRequirementsDetailKeys = ({
  entityId,
  entityName,
} = {}) => ['get-minimum-requirements', entityName, parseInt(entityId)];

export const useGetMinimumRequirementDetail = ({
  entityId,
  entityName = 'Product',
  ...otherProps
} = {}) => {
  const queryClient = useQueryClient();

  const queries = useQuery({
    queryKey: minimumRequirementsDetailKeys({
      entityId,
      entityName,
    }),
    queryFn: async () => {
      const response =
        await minimumRequirementServices.getEntityMinimumRequirementDetail({
          entityId,
          entityName,
        });

      return response?.data;
    },

    ...otherProps,
  });

  const handleValidateMinimumRequirementDetail = () => {
    queryClient.invalidateQueries({
      queryKey: minimumRequirementsDetailKeys({
        entityId,
        entityName,
      }),
    });
  };

  return {
    ...queries,
    entityRequirement: queries?.data ?? {},
    isEmptyEntityRequirement: isEmpty(queries?.data ?? {}),
    handleValidateMinimumRequirementDetail,
  };
};

export const validateMinimumRequirementKeys = ({
  requirementId,
  productId,
} = {}) => [
  'validate-minimum-requirement',
  parseInt(requirementId),
  parseInt(productId),
];

export const useValidateMinimumRequirement = ({
  requirementId,
  productId,
} = {}) => {
  const queries = useQuery({
    queryKey: validateMinimumRequirementKeys({
      requirementId,
      productId,
    }),
    queryFn: async () => {
      const response =
        await minimumRequirementServices.validateMinimumRequirement({
          id: requirementId,
          idsToCheck: [productId],
        });
      return response?.data;
    },
    enabled: Boolean(requirementId) && Boolean(productId),
    staleTime: 0,
  });

  return {
    requiredErrorFields: queries?.data?.results?.[0]?.errorFields ?? [],
    isMatched: queries?.data?.results?.[0]?.matched,
    ...queries,
  };
};

export const useUpdateUnmatchedRequirement = () => {
  return useMutation({
    mutationFn: minimumRequirementServices.updateUnmatchedRequirements,
    onSuccess: async (result) => {
      if (result?.isSuccess) {
        CustomNotification.success('Update successfully.');
      } else {
        CustomNotification.error(result?.message || 'Something went wrong!');
      }
    },
    onError: () => {
      CustomNotification.error({ message: 'Something went wrong!' });
    },
  });
};

export const useCalculateFeeActiveProduct = ({ productIds }) => {
  const { passedProducts } = useValidateActiveProduct({
    productIds,
  });

  return passedProducts.reduce((accumulator, product) => {
    return accumulator + product.fee;
  }, 0);
};
